// Auth Context Provider
// =====================
// Supports both Firebase Auth and Mock Auth (for development without Firebase)

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User, AuthState } from '../types';
import i18n from '../i18n';
import {
  shouldUseMockAuth,
  mockSignIn,
  mockSignUp,
  mockSignOut,
  mockResetPassword,
  mockUpdateUser,
  mockDeleteAccount,
  mockChangePassword,
  onMockAuthChange,
  notifyAuthListeners,
} from '../services/mockAuth';

// Only import Firebase if we're going to use it
let firebaseAuth: typeof import('../services/firebase') | null = null;

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, displayName: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>;
  updateUser: (updates: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  deleteAccount: (password?: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  isMockAuth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });
  const [isMockAuth, setIsMockAuth] = useState<boolean>(true);

  useEffect(() => {
    const useMock = shouldUseMockAuth();
    setIsMockAuth(useMock);

    if (useMock) {
      // Use mock authentication
      console.log('[Auth] Using mock authentication (Firebase not configured)');

      const unsubscribe = onMockAuthChange((user) => {
        setAuthState({
          user,
          loading: false,
          error: null,
        });
      });

      return () => unsubscribe();
    } else {
      // Use Firebase authentication
      console.log('[Auth] Using Firebase authentication');

      // Dynamically import Firebase
      import('../services/firebase').then((firebase) => {
        firebaseAuth = firebase;

        const unsubscribe = firebase.onAuthChange(async (firebaseUser) => {
          if (firebaseUser) {
            // Firebase Auth metadata always has creation time - use as reliable fallback
            const authCreatedAt = firebaseUser.metadata.creationTime
              ? new Date(firebaseUser.metadata.creationTime)
              : new Date();

            // Run localStorage to Firestore migration (Feature #40)
            // This is idempotent - will skip if already migrated
            try {
              const migrationResult = await firebase.runFullMigration(firebaseUser.uid);
              if (!migrationResult.alreadyMigrated) {
                console.log('[Auth] Migration result:', {
                  sessions: `${migrationResult.sessions.migratedCount} migrated, ${migrationResult.sessions.skippedCount} skipped`,
                  journeys: `${migrationResult.journeys.migratedCount} migrated, ${migrationResult.journeys.skippedCount} skipped`,
                  success: migrationResult.success,
                });
              }
            } catch (migrationError) {
              console.error('[Auth] Migration error (non-blocking):', migrationError);
              // Migration errors are non-blocking - user can still continue
            }

            // Run topic migration (Feature #95)
            // Migrates orphaned localStorage topics to Firebase
            try {
              const topicMigrationResult = await firebase.migrateLocalStorageTopicsToFirebase(firebaseUser.uid);
              if (!topicMigrationResult.alreadyMigrated && topicMigrationResult.migratedCount > 0) {
                console.log('[Auth] Topic migration result:', {
                  migrated: topicMigrationResult.migratedCount,
                  skipped: topicMigrationResult.skippedCount,
                });
              }
            } catch (topicMigrationError) {
              console.error('[Auth] Topic migration error (non-blocking):', topicMigrationError);
              // Topic migration errors are non-blocking
            }

            // Fetch user document from Firestore
            const result = await firebase.queryDocuments<User>(
              'users',
              firebase.where('uid', '==', firebaseUser.uid)
            );

            if (result.success && result.data && result.data.length > 0) {
              const userData = result.data[0];
              // Convert Firestore Timestamp to Date if needed,
              // fall back to Firebase Auth creation time (always available)
              const createdAt = userData.createdAt && typeof (userData.createdAt as any).toDate === 'function'
                ? (userData.createdAt as any).toDate()
                : userData.createdAt instanceof Date
                  ? userData.createdAt
                  : authCreatedAt;

              // Sync app language with user's saved preference
              if (userData?.preferences?.language) {
                i18n.changeLanguage(userData.preferences.language);
                localStorage.setItem('lyra-language', userData.preferences.language);
              }

              setAuthState({
                user: {
                  ...userData,
                  createdAt,
                },
                loading: false,
                error: null,
              });
            } else {
              // User exists in Auth but not in Firestore
              setAuthState({
                user: {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || '',
                  displayName: firebaseUser.displayName || '',
                  createdAt: authCreatedAt,
                  onboardingCompleted: false,
                  preferences: { darkMode: false, language: 'da' },
                  adaptiveLevels: { knowledge: 2, language: 2, engagement: 2 },
                },
                loading: false,
                error: null,
              });
            }
          } else {
            setAuthState({
              user: null,
              loading: false,
              error: null,
            });
          }
        });

        return () => unsubscribe();
      }).catch((error) => {
        console.error('[Auth] Failed to load Firebase:', error);
        // Fall back to mock auth
        setIsMockAuth(true);
        const unsubscribe = onMockAuthChange((user) => {
          setAuthState({
            user,
            loading: false,
            error: null,
          });
        });
        return () => unsubscribe();
      });
    }
  }, []);

  const login = async (email: string, password: string) => {
    // Note: We don't set loading: true here because it would unmount components
    // wrapped in PublicRoute (which shows a spinner when loading=true).
    // The form components handle their own loading state.

    if (isMockAuth) {
      const result = await mockSignIn(email, password);
      if (result.success && result.user) {
        setAuthState({
          user: result.user,
          loading: false,
          error: null,
        });
        notifyAuthListeners();
      } else {
        setAuthState((prev) => ({ ...prev, error: result.error || 'Login failed' }));
      }
      return { success: result.success, error: result.error };
    } else if (firebaseAuth) {
      const result = await firebaseAuth.signIn(email, password);
      if (!result.success) {
        setAuthState((prev) => ({ ...prev, error: result.error || null }));
      }
      return result;
    }

    return { success: false, error: 'Auth not initialized' };
  };

  const register = async (email: string, password: string, displayName: string) => {
    // Note: We don't set loading: true here because it would unmount components
    // wrapped in PublicRoute (which shows a spinner when loading=true).
    // The form components handle their own loading state.

    if (isMockAuth) {
      const result = await mockSignUp(email, password, displayName);
      if (result.success && result.user) {
        setAuthState({
          user: result.user,
          loading: false,
          error: null,
        });
        notifyAuthListeners();
      } else {
        setAuthState((prev) => ({ ...prev, error: result.error || 'Registration failed' }));
      }
      return { success: result.success, error: result.error };
    } else if (firebaseAuth) {
      const result = await firebaseAuth.signUp(email, password, displayName);
      if (!result.success) {
        setAuthState((prev) => ({ ...prev, error: result.error || null }));
      }
      return result;
    }

    return { success: false, error: 'Auth not initialized' };
  };

  const logout = async () => {
    if (isMockAuth) {
      await mockSignOut();
      setAuthState({
        user: null,
        loading: false,
        error: null,
      });
      notifyAuthListeners();
    } else if (firebaseAuth) {
      await firebaseAuth.logOut();
      setAuthState({
        user: null,
        loading: false,
        error: null,
      });
    }
  };

  const sendPasswordReset = async (email: string) => {
    if (isMockAuth) {
      return await mockResetPassword(email);
    } else if (firebaseAuth) {
      return await firebaseAuth.resetPassword(email);
    }
    return { success: false, error: 'Auth not initialized' };
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!authState.user) {
      return { success: false, error: 'No user logged in' };
    }

    if (isMockAuth) {
      const result = await mockUpdateUser(authState.user.uid, updates);
      if (result.success) {
        setAuthState((prev) => ({
          ...prev,
          user: prev.user ? { ...prev.user, ...updates } : null,
        }));
      }
      return result;
    } else if (firebaseAuth) {
      // For Firebase, upsert the user document (creates if not exists, updates if exists)
      try {
        console.log('[Auth] Upserting user document with updates:', updates);

        // Use upsertDocument which creates the document if it doesn't exist
        const upsertResult = await firebaseAuth.upsertDocument('users', authState.user.uid, {
          uid: authState.user.uid,
          email: authState.user.email,
          displayName: authState.user.displayName,
          ...updates,
        });

        console.log('[Auth] Upsert result:', upsertResult);

        if (upsertResult.success) {
          setAuthState((prev) => ({
            ...prev,
            user: prev.user ? { ...prev.user, ...updates } : null,
          }));
        }
        return upsertResult;
      } catch (error: any) {
        console.error('[Auth] Error upserting user:', error);
        return { success: false, error: error.message || 'Failed to update user' };
      }
    }

    return { success: false, error: 'Auth not initialized' };
  };

  const deleteAccount = async (password?: string) => {
    if (!authState.user) {
      return { success: false, error: 'No user logged in' };
    }

    if (isMockAuth) {
      const result = await mockDeleteAccount(authState.user.uid);
      if (result.success) {
        setAuthState({
          user: null,
          loading: false,
          error: null,
        });
        notifyAuthListeners();
      }
      return result;
    } else if (firebaseAuth) {
      // For Firebase, password is required for re-authentication
      if (!password) {
        return { success: false, error: 'Adgangskode er påkrævet for at slette konto.' };
      }

      const result = await firebaseAuth.deleteUserAccount(password);
      if (result.success) {
        setAuthState({
          user: null,
          loading: false,
          error: null,
        });
      }
      return result;
    }

    return { success: false, error: 'Auth not initialized' };
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!authState.user) {
      return { success: false, error: 'No user logged in' };
    }

    if (isMockAuth) {
      return await mockChangePassword(authState.user.uid, currentPassword, newPassword);
    } else if (firebaseAuth) {
      return { success: false, error: 'Firebase change password not implemented' };
    }

    return { success: false, error: 'Auth not initialized' };
  };

  const value: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    sendPasswordReset,
    updateUser,
    deleteAccount,
    changePassword,
    isMockAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
